import { memo, useMemo } from 'react';

import {
  Button,
  ButtonProps,
  ButtonSize,
  ButtonType,
  ButtonWithState,
  ButtonWithStateProps,
} from '@xemplo/button';
import { IconButtonSize } from '@xemplo/icon-button';
import { DawnArrowLongLeft } from '@xemplo/icons';

import { DrawerHeaderComponentProps, DrawerMode } from '../drawer.types';

import * as S from './drawer-header.styles';

export const DrawerHeaderTestId = {
  standardWrapper: 'drawer-header-standard-wrapper',
  editorWrapper: 'drawer-header-editor-wrapper',
  title: 'drawer-header-title',
  primaryBtn: 'drawer-header-primary-btn',
  secondaryBtn: 'drawer-header-secondary-btn',
};

export const DrawerHeader = memo((props: DrawerHeaderComponentProps) => {
  const { mode = DrawerMode.Standard, titleId, ...headerProps } = props;

  return mode === DrawerMode.Standard ? (
    <StandardHeader titleId={titleId} {...headerProps} />
  ) : (
    <EditorHeader titleId={titleId} {...headerProps} />
  );
});

function StandardHeader({
  hasBackButton,
  title,
  backButtonClick,
  titleId,
}: Readonly<DrawerHeaderComponentProps>) {
  return (
    <S.DrawerHeaderContent data-testid={DrawerHeaderTestId.standardWrapper}>
      {hasBackButton && (
        <S.BackButton
          id="drawer-back-btn"
          ariaLabel="Back button on drawer"
          size={IconButtonSize.Small}
          naked
          onClick={backButtonClick}
        >
          <DawnArrowLongLeft fontSize={16} />
        </S.BackButton>
      )}
      {title && (
        <S.Title id={titleId} data-testid={DrawerHeaderTestId.title}>
          {title}
        </S.Title>
      )}
    </S.DrawerHeaderContent>
  );
}

/**
 * We purposefully don't allow the user to override some of these props
 * in order to ensure design specs.
 */
function EditorHeader({
  hasBackButton,
  title,
  primaryBtn,
  secondaryBtn,
  backButtonClick,
  titleId,
}: Readonly<DrawerHeaderComponentProps>) {
  const primaryBtnProps = useMemo(() => {
    return {
      ...primaryBtn,
      size: ButtonSize.Small,
      type: ButtonType.Primary,
      id: 'drawer-header-primary-btn',
      ariaLabel: 'Primary button on drawer header',
    } as ButtonWithStateProps;
  }, [primaryBtn]);

  const secondaryBtnProps = useMemo(() => {
    return {
      ...secondaryBtn,
      size: ButtonSize.Small,
      type: ButtonType.Tertiary,
      id: 'drawer-header-secondary-btn',
      ariaLabel: 'Secondary button on drawer header',
    } as ButtonProps;
  }, [secondaryBtn]);

  return (
    <S.DrawerHeaderContent data-testid={DrawerHeaderTestId.editorWrapper}>
      {hasBackButton && (
        <S.BackButton
          id="drawer-back-btn"
          ariaLabel="Back button on drawer"
          size={IconButtonSize.Small}
          naked
          onClick={backButtonClick}
        >
          <DawnArrowLongLeft fontSize={16} />
        </S.BackButton>
      )}
      {title && (
        <S.Title id={titleId} data-testid={DrawerHeaderTestId.title}>
          {title}
        </S.Title>
      )}
      {primaryBtn != null || secondaryBtn != null ? (
        <S.EditorBtnWrapper>
          {secondaryBtn && (
            <Button {...secondaryBtnProps} testId={DrawerHeaderTestId.secondaryBtn}>
              {secondaryBtn.label}
            </Button>
          )}
          {primaryBtn && (
            <ButtonWithState {...primaryBtnProps} testId={DrawerHeaderTestId.primaryBtn}>
              {primaryBtn.label}
            </ButtonWithState>
          )}
        </S.EditorBtnWrapper>
      ) : null}
    </S.DrawerHeaderContent>
  );
}
